import { useQueryClient } from '@tanstack/react-query';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import type { CreateAttributeRequestApi, UpdateAttributeRequestApi } from '../types/attributes-api.types';
import type {
  CustomFieldDefinitionsPagingResult,
  GetCustomFieldDefinitionsParams,
  InsertCustomFieldDefinitionResult,
  UpdateCustomFieldDefinitionResult,
} from '../types/custom-field-definitions.types';
import {
  deleteCustomFieldDefinition,
  getCustomFieldDefinitions,
  insertCustomFieldDefinition,
  updateCustomFieldDefinition,
} from '../services/custom-field-definitions.service';

export const useCustomFieldDefinitions = (
  params: GetCustomFieldDefinitionsParams = {},
  options: { enabled?: boolean } = {}
) => {
  return useGlobalQuery<CustomFieldDefinitionsPagingResult>({
    queryKey: ['custom-field-definitions', params],
    queryFn: () => getCustomFieldDefinitions(params),
    staleTime: 5 * 60 * 1000,
    enabled: options.enabled ?? true,
  });
};

export const useInsertCustomFieldDefinition = () => {
  const queryClient = useQueryClient();

  return useGlobalMutation<InsertCustomFieldDefinitionResult, Error, CreateAttributeRequestApi>({
    mutationFn: insertCustomFieldDefinition,
    suppressToast: true,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-field-definitions'] });
    },
  });
};

export const useUpdateCustomFieldDefinition = () => {
  const queryClient = useQueryClient();

  return useGlobalMutation<
    UpdateCustomFieldDefinitionResult,
    Error,
    { key: string; input: UpdateAttributeRequestApi }
  >({
    mutationFn: updateCustomFieldDefinition,
    suppressToast: true,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-field-definitions'] });
    },
  });
};

export const useDeleteCustomFieldDefinition = () => {
  const queryClient = useQueryClient();

  return useGlobalMutation<UpdateCustomFieldDefinitionResult, Error, string>({
    mutationFn: deleteCustomFieldDefinition,
    suppressToast: true,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-field-definitions'] });
    },
  });
};
